import { Transform } from 'class-transformer';
import {
  IsArray,
  IsBoolean,
  IsDateString,
  IsIn,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
  MinLength,
} from 'class-validator';
import { isConfigKey } from './permits.dto';

/**
 * Checklist inspections — rental housing standards, fire, property standards.
 *
 * A municipality keeps checklist templates; an inspection is scheduled against
 * one for a property (and, where it came from one, a permit application or an
 * enforcement file), carried out in the field app or on the desk, and closes
 * with a pass or a fail and an EN/FR report. A failed inspection can put the
 * property in front of the enforcement team as an internal report in the
 * triage queue — never a file of its own.
 *
 * Distinct from the Stage 3 site-visit inspection on an enforcement file: that
 * one is a statutory step in a by-law case; this is a standards checklist.
 */

export const INSPECTION_CATEGORIES = [
  'rental_housing',
  'fire',
  'property_standards',
  'other',
] as const;
export type InspectionCategory = (typeof INSPECTION_CATEGORIES)[number];

export const INSPECTION_STATUSES = ['scheduled', 'in_progress', 'completed', 'cancelled'] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

export const INSPECTION_ITEM_RESULTS = ['pass', 'fail', 'na'] as const;
export type InspectionItemResult = (typeof INSPECTION_ITEM_RESULTS)[number];

export type InspectionOutcome = 'pass' | 'fail';

export interface InspectionChecklistItem {
  key: string;
  sectionEn?: string;
  sectionFr?: string;
  labelEn: string;
  labelFr: string;
  guidanceEn?: string;
  guidanceFr?: string;
}

export interface InspectionItemRecord {
  result: InspectionItemResult | null;
  note: string | null;
  /** Device time of the answer. The later answer wins, item by item. */
  recordedAt: string;
}

/** One answer as the field app or the desk sends it. */
export interface InspectionResultInput {
  itemKey: string;
  result: InspectionItemResult | null;
  note?: string | null;
  recordedAt: string;
}

export const INSPECTION_NEXT: Record<InspectionStatus, readonly InspectionStatus[]> = {
  scheduled: ['in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

export function canMoveInspection(from: InspectionStatus, to: InspectionStatus): boolean {
  return INSPECTION_NEXT[from].includes(to);
}

/** Problems with a template's items, empty when valid. */
export function inspectionTemplateProblems(items: InspectionChecklistItem[]): string[] {
  const problems: string[] = [];
  if (items.length === 0) problems.push('a checklist needs at least one item');
  if (items.length > 300) problems.push('a checklist may have at most 300 items');
  const seen = new Set<string>();
  for (const [i, item] of items.entries()) {
    const at = `items[${i}]`;
    if (!item || typeof item !== 'object') {
      problems.push(`${at} must be an object`);
      continue;
    }
    if (typeof item.key !== 'string' || !isConfigKey(item.key))
      problems.push(`${at}.key is invalid`);
    else if (seen.has(item.key)) problems.push(`${at}.key ${item.key} is repeated`);
    else seen.add(item.key);
    if (!item.labelEn?.trim() || !item.labelFr?.trim()) problems.push(`${at} needs both labels`);
    if (Boolean(item.sectionEn?.trim()) !== Boolean(item.sectionFr?.trim())) {
      problems.push(`${at} needs the section in both languages or neither`);
    }
  }
  return problems;
}

/** Problems with a batch of answers against the inspection's own items. */
export function resultProblems(
  items: readonly InspectionChecklistItem[],
  results: readonly InspectionResultInput[],
): string[] {
  const keys = new Set(items.map((i) => i.key));
  const problems: string[] = [];
  for (const [i, r] of results.entries()) {
    const at = `results[${i}]`;
    if (!r || typeof r !== 'object') {
      problems.push(`${at} must be an object`);
      continue;
    }
    if (!keys.has(r.itemKey)) problems.push(`${at}.itemKey is not on this checklist`);
    if (r.result !== null && !(INSPECTION_ITEM_RESULTS as readonly string[]).includes(r.result)) {
      problems.push(`${at}.result is invalid`);
    }
    if (r.note != null && (typeof r.note !== 'string' || r.note.length > 4000)) {
      problems.push(`${at}.note is invalid`);
    }
    if (typeof r.recordedAt !== 'string' || Number.isNaN(Date.parse(r.recordedAt))) {
      problems.push(`${at}.recordedAt is invalid`);
    }
  }
  return problems;
}

/**
 * Fold answers into the stored results, item by item, the later `recordedAt`
 * winning. Replaying a queued batch is therefore harmless, and an older answer
 * from a phone that was offline never overwrites a newer one given since.
 */
export function mergeResults(
  existing: Record<string, InspectionItemRecord>,
  incoming: readonly InspectionResultInput[],
): Record<string, InspectionItemRecord> {
  const next = { ...existing };
  for (const r of incoming) {
    const current = next[r.itemKey];
    if (current && Date.parse(current.recordedAt) > Date.parse(r.recordedAt)) continue;
    next[r.itemKey] = {
      result: r.result,
      note: r.note?.trim() ? r.note.trim() : null,
      recordedAt: new Date(r.recordedAt).toISOString(),
    };
  }
  return next;
}

/** Items with no answer yet. */
export function unansweredItems(
  items: readonly InspectionChecklistItem[],
  results: Record<string, InspectionItemRecord>,
): string[] {
  return items.filter((i) => !results[i.key]?.result).map((i) => i.key);
}

/** Failed items, in checklist order. */
export function failedItems(
  items: readonly InspectionChecklistItem[],
  results: Record<string, InspectionItemRecord>,
): InspectionChecklistItem[] {
  return items.filter((i) => results[i.key]?.result === 'fail');
}

/** Pass or fail once every item is answered; null while any is open. */
export function inspectionOutcome(
  items: readonly InspectionChecklistItem[],
  results: Record<string, InspectionItemRecord>,
): InspectionOutcome | null {
  if (unansweredItems(items, results).length > 0) return null;
  return failedItems(items, results).length > 0 ? 'fail' : 'pass';
}

export function inspectionReference(id: string): string {
  return `INS-${id.slice(0, 8).toUpperCase()}`;
}

// ── Views ─────────────────────────────────────────────────────────────────────

export interface InspectionTemplateView {
  id: string;
  localGovernmentId: string;
  category: InspectionCategory;
  nameEn: string;
  nameFr: string;
  items: InspectionChecklistItem[];
  isActive: boolean;
  updatedAt: string;
}

export interface InspectionPhotoView {
  id: string;
  itemKey: string;
  clientId: string;
  url: string;
  createdAt: string;
}

export interface InspectionRow {
  id: string;
  reference: string;
  localGovernmentId: string;
  status: InspectionStatus;
  outcome: InspectionOutcome | null;
  scheduledFor: string;
  templateNameEn: string;
  templateNameFr: string;
  propertyAddress: string;
  inspectorId: string | null;
  inspectorName: string | null;
  /** What it is linked to, for the list's subtitle. */
  permitApplicationReference: string | null;
  fileReference: string | null;
}

export interface InspectionView extends InspectionRow {
  templateId: string | null;
  pid: string | null;
  fileId: string | null;
  permitApplicationId: string | null;
  permitTypeNameEn: string | null;
  permitTypeNameFr: string | null;
  items: InspectionChecklistItem[];
  results: Record<string, InspectionItemRecord>;
  photos: InspectionPhotoView[];
  summary: string | null;
  startedAt: string | null;
  completedAt: string | null;
  cancelledReason: string | null;
  /** The triage-queue report raised from this inspection, if any. */
  complaintReference: string | null;
  updatedAt: string;
}

/** Someone an inspection can be assigned to at the municipality. */
export interface InspectorOption {
  id: string;
  name: string;
}

export interface InspectionModuleStatus {
  enabled: boolean;
}

// ── Inputs ────────────────────────────────────────────────────────────────────

const trim = ({ value }: { value: unknown }) => (typeof value === 'string' ? value.trim() : value);
const trimOptional = ({ value }: { value: unknown }) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

export class SaveInspectionTemplateDto {
  @IsIn(INSPECTION_CATEGORIES)
  category!: InspectionCategory;

  @Transform(trim)
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  nameEn!: string;

  @Transform(trim)
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  nameFr!: string;

  /** Checked by {@link inspectionTemplateProblems} in the service. */
  @IsArray()
  items!: InspectionChecklistItem[];

  @IsBoolean()
  isActive!: boolean;
}

export class ScheduleInspectionDto {
  @IsUUID()
  templateId!: string;

  @IsDateString()
  scheduledFor!: string;

  @IsOptional()
  @IsUUID()
  inspectorId?: string;

  /** Defaults to the application's or the file's address when linked. */
  @IsOptional()
  @Transform(trimOptional)
  @IsString()
  @MinLength(5)
  @MaxLength(400)
  propertyAddress?: string;

  @IsOptional()
  @Transform(trimOptional)
  @IsString()
  @MaxLength(32)
  pid?: string;

  @IsOptional()
  @IsUUID()
  fileId?: string;

  @IsOptional()
  @IsUUID()
  permitApplicationId?: string;
}

export class UpdateInspectionDto {
  @IsOptional()
  @IsDateString()
  scheduledFor?: string;

  /** Null clears the inspector. */
  @IsOptional()
  @IsUUID()
  inspectorId?: string | null;
}

/** Answers, as a batch. Validated item by item by {@link resultProblems}. */
export class RecordInspectionResultsDto {
  @IsArray()
  results!: InspectionResultInput[];

  @IsOptional()
  @IsString()
  @MaxLength(8000)
  summary?: string | null;
}

export class CompleteInspectionDto {
  @IsOptional()
  @IsString()
  @MaxLength(8000)
  summary?: string | null;
}

export class CancelInspectionDto {
  @Transform(trim)
  @IsString()
  @MinLength(3)
  @MaxLength(2000)
  reason!: string;
}

/** Multipart fields that travel with an item photo. */
export class UploadInspectionPhotoDto {
  @IsString()
  @MaxLength(64)
  itemKey!: string;

  @IsString()
  @MinLength(8)
  @MaxLength(64)
  clientId!: string;
}
